import {
	Children,
	cloneElement,
	FC,
	isValidElement,
	ReactElement,
	useEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

import StyledTooltip, { StyledTooltipPointer } from "./styling/Tooltip.styles";
import {
	TooltipProps,
	TooltipTriggerCoordinates,
	TooltipPlacementOptions,
	TooltipDimensions,
} from "./Tooltip.types";

const TOOLTIP_POINTER_HEIGHT = 8;
const TOOLTIP_POINTER_SPACE = 4;

const Tooltip: FC<TooltipProps> = ({
	text,
	placement = "top",
	alignment = "center",
	delay = 0,
	children,
	colorOn = "bg_white",
}) => {
	const [mouseOver, setMouseOver] = useState(false);

	const [tooltipContainer, setTooltipContainer] = useState(
		typeof window === "undefined" ? null : document.createElement("div")
	);

	const [showTooltip, setShowTooltip] = useState<boolean>(false);

	const [tooltipDimensions, setTooltipDimensions] = useState<TooltipDimensions>(
		{
			width: 0,
			height: 0,
		}
	);

	const [tooltipPlacement, setTooltipPlacement] =
		useState<TooltipPlacementOptions>(placement);

	const [triggerCoordinates, setTriggerCoordinates] =
		useState<TooltipTriggerCoordinates>({
			top: 0,
			left: 0,
			width: 0,
			height: 0,
		});

	const tooltipRef = useRef<HTMLDivElement | null>(null);

	const displayTooltip = (event: MouseEvent) => {
		const triggerNode = event.currentTarget as HTMLElement;
		const rect = triggerNode.getBoundingClientRect();

		setTriggerCoordinates({
			left: rect.x,
			top: rect.y,
			width: rect.width,
			height: rect.height,
		});
		setMouseOver(true);
		setTimeout(() => setShowTooltip(true), 0);
	};

	const hideTooltip = () => {
		setShowTooltip(false);
		setTimeout(() => setMouseOver(false), 200);
	};

	const triggerNode = Children.map(children, (child, index) => {
		if (isValidElement(child)) {
			return (
				isValidElement(child) &&
				cloneElement(child as ReactElement, {
					key: index,
					onMouseEnter: displayTooltip,
					onMouseLeave: hideTooltip,
					onFocus: displayTooltip,
					onBlur: hideTooltip,
				})
			);
		}
	});

	useEffect(() => {
		window.addEventListener("scroll", hideTooltip);
		return () => window.removeEventListener("scroll", hideTooltip);
	}, []);

	useEffect(() => {
		if (!tooltipContainer) {
			setTooltipContainer(document.createElement("div"));
			return;
		}
		document.body.appendChild(tooltipContainer);
		return () => {
			document.body.removeChild(tooltipContainer);
		};
	}, [tooltipContainer]);

	useEffect(() => {
		if (mouseOver && tooltipRef.current) {
			const tooltipNode = tooltipRef.current;
			const rect = tooltipNode.getBoundingClientRect();

			const tooltipWidth = rect.width;
			const tooltipHeight = rect.height;
			const totalTooltipHeight =
				tooltipHeight + TOOLTIP_POINTER_HEIGHT + TOOLTIP_POINTER_SPACE;
			const totalTooltipWidth =
				tooltipWidth + TOOLTIP_POINTER_HEIGHT + TOOLTIP_POINTER_SPACE;

			const relativePlacement = {
				top:
					triggerCoordinates.top >= totalTooltipHeight ? placement : "bottom",
				bottom:
					window.innerHeight -
						triggerCoordinates.height -
						triggerCoordinates.top >=
					totalTooltipHeight
						? placement
						: "top",
				left:
					triggerCoordinates.left >= totalTooltipWidth ? placement : "right",
				right:
					window.innerWidth - triggerCoordinates.left >= totalTooltipWidth
						? placement
						: "left",
			}[placement];

			setTooltipPlacement(relativePlacement);
			setTooltipDimensions({
				width: tooltipWidth,
				height: tooltipHeight,
			});
		}
	}, [triggerCoordinates, mouseOver, placement, tooltipRef]);

	return (
		<>
			{triggerNode}
			{mouseOver &&
				tooltipContainer &&
				createPortal(
					<StyledTooltip
						ref={tooltipRef}
						triggerCoordinates={triggerCoordinates}
						tooltipDimensions={tooltipDimensions}
						colorOn={colorOn}
						showTooltip={showTooltip}
						placement={tooltipPlacement}
						alignment={alignment}
						delay={delay}
					>
						{text}
						<StyledTooltipPointer
							placement={tooltipPlacement}
							alignment={alignment}
							triggerCoordinates={triggerCoordinates}
						/>
					</StyledTooltip>,
					tooltipContainer
				)}
		</>
	);
};

export default Tooltip;
